import React, { useEffect, useState } from "react";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateOverlappingPayment } from "common/services/payments.service";
import { toast } from "react-toastify";
import { OVERLAP_STATUS_OPTIONS } from "common/constants/overlap-status";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

/**
 * Applies one status + one set of comments to many overlapping payments.
 *
 * "resolve" only ever targets unresolved records and "edit" only ever targets
 * already-resolved ones; the ineligible half of the selection is filtered out
 * by the caller and reported here as a skip count so the user can see why the
 * number of affected rows is smaller than what they ticked.
 */
const BulkResolvePayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
  eligibleIds: string[];
  skippedCount: number;
  mode: "resolve" | "edit";
  onCompleted: () => void;
}> = ({
  toggle,
  isModalOpen,
  fetchData,
  eligibleIds,
  skippedCount,
  mode,
  onCompleted,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const count = eligibleIds.length;
  const isEdit = mode === "edit";
  const plural = count === 1 ? "" : "s";

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: "",
      overlapStatus: "",
    },
    validationSchema: Yup.object({
      comments: Yup.string(),
      overlapStatus: Yup.string()
        .oneOf(
          OVERLAP_STATUS_OPTIONS.map((option) => option.value),
          "Please select a valid status"
        )
        .required("Please select a status"),
    }),
    onSubmit: async (values: any) => {
      if (!count) return;

      const data = {
        data: {
          comments: values.comments,
          overlapStatus: values.overlapStatus,
          resolved: true,
        },
      };

      setLoading(true);
      try {
        await Promise.all(
          eligibleIds.map((id) => updateOverlappingPayment(id, data))
        );
        fetchData();
        onCompleted();
        validation.resetForm();
        toggle();
        toast.success(
          isEdit
            ? `${count} resolution${plural} updated successfully`
            : `${count} payment${plural} resolved successfully`
        );
      } catch (e: any) {
        toast.error(
          isEdit
            ? "Bulk resolution update failed"
            : "Bulk payment resolution failed"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Values are shared across both modes, so start from a clean form each time
  // rather than carrying the previous action's status/comments over.
  useEffect(() => {
    if (isModalOpen) {
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, mode]);

  return (
    <Modal
      id="bulkResolveModal"
      size="lg"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        {isEdit
          ? `Edit Resolution for ${count} Payment${plural}`
          : `Resolve ${count} Duplicate Payment${plural}`}
      </ModalHeader>

      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <p className="text-muted">
            This status and these comments will {isEdit ? "replace the " : ""}
            {isEdit ? "existing resolution on" : "be applied to"} all {count}{" "}
            selected payment{plural}.
          </p>

          {skippedCount > 0 && (
            <div className="alert alert-warning" role="alert">
              <i className="ri-information-line align-bottom me-1"></i>
              {skippedCount} selected payment{skippedCount === 1 ? "" : "s"}{" "}
              {skippedCount === 1 ? "is" : "are"}{" "}
              {isEdit ? "not resolved yet" : "already resolved"} and will be
              skipped.
            </div>
          )}

          <Row>
            <Col md={12} className="mb-4">
              <Label htmlFor="bulkOverlapStatus" className="form-label">
                What is this payment?
              </Label>
              <Input
                name="overlapStatus"
                id="bulkOverlapStatus"
                type="select"
                className="form-select"
                value={validation.values.overlapStatus}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.overlapStatus &&
                  !!validation.errors.overlapStatus
                }
              >
                <option value="">Select a status</option>
                {OVERLAP_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
              {validation.touched.overlapStatus &&
              validation.errors.overlapStatus ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors.overlapStatus}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md={12} className="mb-3">
              <Label htmlFor="bulk-comments-field" className="form-label">
                Comments on resolution (Optional)
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={validation.values.comments || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  validation.setFieldValue("comments", data);
                }}
                onBlur={() => {
                  validation.handleBlur("comments");
                }}
              />
              {validation.touched.comments && validation.errors.comments ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors?.comments}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </ModalBody>

        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                toggle();
              }}
            >
              Close
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || count === 0}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                ></Spinner>
              )}
              {isEdit
                ? `Update ${count} Resolution${plural}`
                : `Resolve ${count} Payment${plural}`}
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BulkResolvePayment;
